import * as React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';

import GalleryModalAsset from '../gallery-modal-asset/GalleryModalAsset';
import GalleryModalArrow from '../gallery-modal-arrow/GalleryModalArrow';
import GalleryModalThumbnail from '../gallery-modal-thumbnail/GalleryModalThumbnail';

import type { GalleryModalItem } from 'types';
import type { ConsentReducerConsentsMap } from 'redux-store/types/ConsentReducer.type';

const SCROLL_STEP: number = 88; // value should match `width` of `.gallery-modal__thumb` class
const NUM_THUMBNAILS_TO_SHOW: number = 9;

type ArrowType = 'thumbnail' | 'main';
type ArrowDirection = 'prev' | 'next';

type OwnProps = {
  className: string,
  itemClassName: string,
  items: Array<GalleryModalItem>,
  currentPosition: number,
  renderCustomAsset?: (c: GalleryModalItem, b: number, a?: (() => void) | null) => React.ReactNode,
  renderCustomThumbnailOverlay?: (a: GalleryModalItem) => React.ReactNode,
  onLastPage?: () => void,
  onCloseModal?: () => void // coming from ModalHOC
};

type StateProps = {
  userChannelConsents: ConsentReducerConsentsMap
};

type Props = OwnProps & StateProps;

type State = {
  currentPosition: number,
  scrollPosition: number,
  canScroll: {
    prev: boolean,
    next: boolean
  },
  diff: number
};

class GalleryModal extends React.PureComponent<Props, State> {
  static defaultProps = {
    className: '',
    itemClassName: ''
  };

  state = {
    currentPosition: 0,
    scrollPosition: 0,
    canScroll: {
      prev: false,
      next: false
    },
    diff: 0
  };

  thumbsContainer: HTMLDivElement | undefined | null = null;

  thumbs: HTMLUListElement | undefined | null = null;

  constructor(props: Props) {
    super(props);

    this.state.currentPosition = props.currentPosition;
  }

  componentDidMount(): void {
    this.calcScroll(0);
    this.handleAutoScroll();

    if (typeof window === 'object' && window.addEventListener) {
      window.addEventListener('keydown', this.handleKeypress);
    }
  }

  componentDidUpdate(prevProps: Props): void {
    // force repaint if items was changed
    if (prevProps.items.length !== this.props.items.length) {
      this.calcScroll(this.state.scrollPosition);
    }
  }

  componentWillUnmount(): void {
    if (typeof window === 'object' && window.removeEventListener) {
      window.removeEventListener('keydown', this.handleKeypress);
    }
  }

  checkCurrentPage = () => {
    const { canScroll } = this.state;
    const { onLastPage } = this.props;
    if (onLastPage && !canScroll.next) {
      onLastPage();
    }
  };

  calcScroll(position: number = 0): void {
    if (this.thumbsContainer && this.thumbs) {
      const width = this.thumbsContainer.clientWidth;
      const maxWidth = this.thumbs.clientWidth;
      const diff = Math.max(maxWidth - width, 0);
      const isScrollNeeded = maxWidth !== width;

      this.setState({
        scrollPosition: position,
        canScroll: {
          prev: isScrollNeeded && position > 0,
          next: isScrollNeeded && (position < diff)
        },
        diff
      }, this.checkCurrentPage);

      if (this.thumbs && this.thumbs.style) {
        this.thumbs.style.transform = `translateX(${-position}px)`;
      }
    }
  }

  // get the number of items that will be displayed after auto-scroll has completed
  getNumItemsDisplayedAfterScrolling = (): number => {
    const { items } = this.props;
    const { currentPosition } = this.state;

    return items.length - currentPosition;
  };

  // get the number of items required so NUM_THUMBNAILS_TO_SHOW are displayed after auto-scrolling
  getNumItemsNeededToDisplayDefaultThumbCount(): number {
    const totalItemsDisplayedAfterScrolling = this.getNumItemsDisplayedAfterScrolling();

    return totalItemsDisplayedAfterScrolling < NUM_THUMBNAILS_TO_SHOW ?
      NUM_THUMBNAILS_TO_SHOW - totalItemsDisplayedAfterScrolling :
      0;
  }

  handleAutoScroll = (): void => {
    const { currentPosition } = this.state;

    if (currentPosition > NUM_THUMBNAILS_TO_SHOW - 1) {
      const numAdditionalItemsNeeded = this.getNumItemsNeededToDisplayDefaultThumbCount();
      const scrollAmountToSubtract = numAdditionalItemsNeeded * SCROLL_STEP;

      this.calcScroll(currentPosition * SCROLL_STEP - scrollAmountToSubtract);
    }
  };

  handleThumbnailScrollPrev = (decrement: boolean = false): void => {
    const { currentPosition } = this.state;

    this.calcScroll(Math.max(this.state.scrollPosition - SCROLL_STEP, 0));

    if (decrement) {
      this.setState({
        currentPosition: currentPosition - 1
      });
    }
  };

  handleThumbnailScrollNext = (increment: boolean = false): void => {
    const { currentPosition } = this.state;

    this.calcScroll(Math.min(this.state.scrollPosition + SCROLL_STEP, this.state.diff));

    if (increment) {
      this.setState({
        currentPosition: currentPosition + 1
      });
    }
  };

  handleArrowClick = (event: React.MouseEvent): void => {
    const target = event.currentTarget;
    const currentDirection = target instanceof HTMLElement && target.dataset.direction;
    const updatePosition = target instanceof HTMLElement && target.dataset.updatePosition;
    const shouldUpdatePosition = updatePosition === 'true';

    if (currentDirection === 'prev') {
      this.handleThumbnailScrollPrev(shouldUpdatePosition);
    } else {
      this.handleThumbnailScrollNext(shouldUpdatePosition);
    }
  };

  handleKeypress = (event: KeyboardEvent): void => {
    const { items } = this.props;
    const { currentPosition } = this.state;
    const keyCode = event.keyCode;

    // 37 = left arrow key, 39 = right arrow key
    if (keyCode === 37 && currentPosition > 0) {
      this.handleThumbnailScrollPrev(true);
    } else if (keyCode === 39 && currentPosition < items.length - 1) {
      this.handleThumbnailScrollNext(true);
    }
  };

  handleThumbnailClick = (event: React.MouseEvent): void => {
    const target = event.currentTarget;
    const currentPosition = (target instanceof HTMLElement && Number(target.dataset.position)) || 0;

    this.setState({
      currentPosition
    });
  };

  renderArrow(type: ArrowType, direction: ArrowDirection, isDisabled: boolean): React.ReactNode {
    const sizes = {
      main: { width: 24, height: 24 },
      thumbnail: { width: 6, height: 13 }
    };

    return (
      <GalleryModalArrow
        type={type}
        direction={direction}
        size={sizes[type]}
        isDisabled={isDisabled}
        updateItemPosition={type === 'main'}
        onClick={this.handleArrowClick}
      />
    );
  }

  renderMainAsset(): React.ReactNode {
    const { items, renderCustomAsset, onCloseModal } = this.props;
    const { currentPosition } = this.state;
    const selectedItem = items[currentPosition];

    if (!selectedItem) {
      return null;
    }

    if (renderCustomAsset) {
      return renderCustomAsset(selectedItem, currentPosition, onCloseModal);
    }

    return (
      <GalleryModalAsset
        selectedItem={selectedItem}
        currentPosition={currentPosition}
      />
    );
  }

  renderThumbnail = (item: GalleryModalItem, index: number): JSX.Element => {
    const {
      renderCustomThumbnailOverlay,
      userChannelConsents
    } = this.props;
    const { currentPosition } = this.state;

    const thumbClasses = classnames({
      'gallery-modal__thumb-background': true,
      'gallery-modal__thumb-background--selected': currentPosition === index
    });

    return (
      <GalleryModalThumbnail
        key={`gallery-modal__thumb-${index + 1}`}
        userChannelConsents={userChannelConsents}
        thumbClasses={thumbClasses}
        renderCustomThumbnailOverlay={renderCustomThumbnailOverlay}
        item={item}
        index={index}
        onThumbnailClick={this.handleThumbnailClick}
      />
    );
  };

  render(): React.ReactNode {
    const { items, className, itemClassName } = this.props;
    const { currentPosition, canScroll } = this.state;

    const classes = classnames({
      'gallery-modal': true,
      [className]: Boolean(className)
    });

    const thumbsContainerClasses = classnames({
      'gallery-modal__thumbs-container': true,
      'gallery-modal__thumbs-container--center': items.length < 9
    });
    const itemContainerClasses = classnames({
      'gallery-modal__active-item-container': true,
      [itemClassName]: Boolean(itemClassName)
    });

    return (
      <section className={classes}>
        <div className={itemContainerClasses}>
          {this.renderArrow('main', 'prev', currentPosition === 0)}
          {this.renderMainAsset()}
          {this.renderArrow('main', 'next', currentPosition === items.length - 1)}
        </div>
        <div className="gallery-modal__tray">
          {this.renderArrow('thumbnail', 'prev', !canScroll.prev)}
          <div
            className={thumbsContainerClasses}
            ref={ref => { this.thumbsContainer = ref; }}
          >
            <ul
              className="gallery-modal__thumbs"
              ref={ref => { this.thumbs = ref; }}
            >
              {
                items.map(this.renderThumbnail)
              }
            </ul>
          </div>
          {this.renderArrow('thumbnail', 'next', !canScroll.next)}
        </div>
      </section>
    );
  }
}

const mapStateToProps = ({ consentReducer }): StateProps => ({
  userChannelConsents: consentReducer.consents
});

export default connect(mapStateToProps)(GalleryModal);
